import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";
import { Offer_findSingleOffer } from "_graphql/visal/queries/__generated__/Offer";
import {
  ActionButton,
  Avatar,
  OfficeButton,
  Pie,
  RealTimeWrapper,
  TableComponent,
} from "components";
import { Action } from "components/buttons/action-button";
import config from "config";
import { Crown, UserPlus } from "lucide-react";
import numeral from "numeral";
import { Fragment } from "react";
import { withPermissions } from "utils";
import AddParticipantsPercentageContainer from "../add-percentage";
import { Colors } from "_graphql/data";
import RemoveContainer from "../remove-participants";

type Props = {
  offer: Offer_findSingleOffer | undefined;
  refetch: () => void;
  loading: boolean;
};

const ReinsurersList = ({ offer, refetch, loading }: Props) => {
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();

  const dispatchAction =
    (
      id: string,
      action: Exclude<Action, "expand" | "goto" | "clone">,
      participation_id?: string,
      isKavaPremiumUser?: boolean
    ) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          reinsurer: participation_id,
          modal: action,
          premium: isKavaPremiumUser ? "premium" : "",
        }),
      });
    };

  const participants =
    offer?.offer_participant?.map((item, idx) => ({
      bgColor: Colors[idx],
      color: Colors[idx],
      id: item?.reinsurer?.re_company_name || "",
      label: item?.reinsurer?.re_company_name || "",
      value: item?.offer_participant_percentage || 0,
    })) || [];

  const sharedValue =
    offer?.offer_participant
      ?.reduce(
        (acc, curr) => acc + (curr?.offer_participant_percentage || 0),
        0
      )
      .toFixed(4) || 0;

  return (
    <Fragment>
      <div className="bg-card-700 shadow rounded-lg">
        <Disclosure as="div" defaultOpen={true}>
          {({ open }) => (
            <>
              <Disclosure.Button
                as="div"
                className="flex justify-between items-center cursor-pointer w-full px-4 py-3"
              >
                <div>
                  <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                    Reinsurers List
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    List of reinsurers who have been added to the offer
                  </p>
                </div>
                <div>
                  {open ? (
                    <ChevronUpIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
              </Disclosure.Button>
              <Disclosure.Panel
                as="div"
                className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="flex flex-col items-center justify-center py-4">
                    <span className="text-center text-gray-600 dark:text-gray-300 text-sm font-bold mb-4">
                      Percentage Left:{" "}
                      {numeral(offer?.facultative_offer)
                        .subtract(sharedValue)
                        .format("0.0000")}{" "}
                      %
                    </span>
                    <div className="w-full max-w-[240px] h-60">
                      <Pie
                        data={[
                          ...participants,
                          {
                            label: "Unassigned",
                            value: Math.max(
                              0,
                              (offer?.facultative_offer || 0) -
                                participants.reduce(
                                  (acc, curr) => acc + curr.value,
                                  0
                                )
                            ),
                            color: "#ccc",
                            bgColor: "#ccc",
                            id: "Unassigned",
                          },
                        ]}
                        innerRadius={0.2}
                      />
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <RealTimeWrapper
                      channel={config.pusher.channels.kava}
                      callBack={refetch}
                      events={[
                        "reinsurer.remove_interest",
                        "reinsurer.thread_notification",
                        "broker.remove_reinsurer_interest",
                        "broker.accepted_interest",
                        "broker.thread_notification",
                        "broker.published_offer",
                      ]}
                    >
                      <TableComponent
                        title={"participants"}
                        hasSearch
                        renderActions={() => (
                          <div className="flex space-x-1">
                            <OfficeButton
                              size="sm"
                              onClick={() =>
                                navigate({
                                  search(prev) {
                                    return {
                                      ...prev,
                                      modal: "create",
                                    };
                                  },
                                })
                              }
                            >
                              <UserPlus className="w-5 h-5 mr-2" />
                              <span className="inline text-xs sm:hidden">
                                Add
                              </span>
                              <span className="hidden sm:inline">
                                Add Participant
                              </span>
                            </OfficeButton>
                          </div>
                        )}
                        loading={loading}
                        columns={[
                          {
                            accessor: "reinsurer.re_company_name",
                            name: "Company Name",
                            render: (row, index) => (
                              <div className="flex items-center justify-start gap-x-2">
                                <Avatar
                                  color={Colors.at(index!)!}
                                  alt={row.reinsurer?.re_company_name}
                                />
                                <span>{row.reinsurer?.re_company_name} </span>
                                {row.reinsurer.kava_premium_member
                                  ?.membership_status === "ACTIVE" && (
                                  <Crown size={16} color="yellow" />
                                )}
                              </div>
                            ),
                          },
                          {
                            accessor: "reinsurer.re_company_email",
                            name: "Company Email",
                          },
                          {
                            accessor: "offer_participant_percentage",
                            name: "Participant %",
                          },
                          {
                            accessor: "",
                            name: "Actions",
                            render: (row) => (
                              <div className="flex space-x-1">
                                {withPermissions([
                                  "*:*",
                                  "offers:*",
                                  "offers:manage",
                                  "endorsements:*",
                                  "endorsements:manage",
                                ])(
                                  <ActionButton
                                    action={"assignPercentage"}
                                    tooltip={
                                      row?.offer_participant_percentage
                                        ? "Update Percentage"
                                        : "Add Percentage"
                                    }
                                    onClick={dispatchAction(
                                      row?.offer_participant_id || "",
                                      "assignPercentage",
                                      numeral(offer?.facultative_offer)
                                        .subtract(sharedValue)
                                        .add(row?.offer_participant_percentage)
                                        .format("#,#.####"),
                                      row?.reinsurer?.kava_premium_member
                                        ?.membership_status === "ACTIVE"
                                    )}
                                  />
                                )}
                                {withPermissions([
                                  "*:*",
                                  "offers:manage",
                                  "offers:delete",
                                  "endorsements:manage",
                                  "endorsements:delete",
                                ])(
                                  <ActionButton
                                    disabled={
                                      row.reinsurer.kava_premium_member
                                        ?.membership_status === "ACTIVE"
                                    }
                                    action={"remove"}
                                    tooltip={"Remove participant"}
                                    onClick={dispatchAction(
                                      row?.offer_participant_id || "",
                                      "remove",
                                      row?.reinsurer?.reinsurer_id || ""
                                    )}
                                  />
                                )}
                              </div>
                            ),
                          },
                        ]}
                        data={offer?.offer_participant || []}
                        refetch={refetch}
                      />
                    </RealTimeWrapper>
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>

      {searchParams?.id && searchParams?.modal === "remove" && (
        <RemoveContainer
          open={searchParams?.modal === "remove"}
          setOpen={(val: boolean) => {
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "remove" : undefined,
                  id: val ? prev?.id : undefined,
                };
              },
            });
          }}
        />
      )}
      {searchParams?.id && searchParams?.modal === "assignPercentage" && (
        <>
          <AddParticipantsPercentageContainer
            open={searchParams?.modal === "assignPercentage"}
            setOpen={(val: boolean) =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    modal: val ? "assignPercentage" : undefined,
                    id: val ? searchParams.id : undefined,
                    reinsurer: val ? searchParams.reinsurer : undefined,
                    premium: val ? searchParams.premium : undefined,
                  };
                },
              })
            }
          />
        </>
      )}
    </Fragment>
  );
};

export default ReinsurersList;
